import axios from "axios";
import { Icon } from "best-common-react";
import React, { useEffect, useRef, useState } from "react";
import { Overlay } from "react-bootstrap";
import styled from "styled-components";
import { downloadFile } from "../../../api/FileApi";
import { getLkupByName } from "../../../api/LookupApi";
import {
    cancelConsentForms,
    getActiveConsentForms,
    getPersonnelById,
} from "../../../api/PersonnelApi";
import { resendQuestionnaire } from "../../../api/QuestionnaireApi";
import { getPersonnelActivityFeed } from "../../../api/RosterApi";
import { useAlert } from "../../../contexts/AlertsContext";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import { BackgroundCheckStatus, BackgroundCheckStatusList } from "../../../types/backgroundCheck";
import { DateUtil } from "../../../util/DateUtil";
import FileUtil from "../../../util/FileUtil";
import FormUtil from "../../../util/FormUtil";
import { Lookup, createLookup } from "../../main/ts/Utils";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";

const SERVICES_URL = process.env.SERVICES_URL;
const MANUAL_FIX_API_URL = `${SERVICES_URL}/manual-fix`;

const formatDate = (dateString: string): string => {
    if (!dateString) return "In Progress";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
    });
};

const ContentContainer = styled.div<{ isStandalone?: boolean }>`
    ${props =>
        props.isStandalone
            ? `
        height: 600px;
        overflow-y: auto;
        overflow-x: visible;
    `
            : `
        max-height: 100%;
        overflow-y: auto;
        overflow-x: visible;
    `}
`;

const PersonnelInfoSection = styled.div.attrs(() => ({
    className: "mb-3",
}))`
    padding-bottom: 8px;
`;

const PersonnelNameStyle = styled.div.attrs(() => ({ className: "mb-1" }))`
    color: var(--slate);
    font-size: 16px;
    font-weight: bold;
`;

const SosReasonStyle = styled.div.attrs(() => ({ className: "mb-1" }))`
    color: var(--slate);
    font-size: 14px;
    font-style: italic;
`;

const CategorySection = styled.div.attrs(() => ({
    className: "mb-3",
}))`
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px;
    background-color: #fafafa;
`;

const CategoryTitle = styled.div`
    color: #92a5ba;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 12px;
`;

const RadioGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const RadioOption = styled.div`
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const StyledRadioButtonText = styled.div`
    font-family: Helvetica;
    font-weight: normal;
    font-size: 14px;
    color: var(--slate);
`;

const DropdownContainer = styled.div`
    display: flex;
    align-items: center;
    min-width: 200px;
`;

const DropdownSelect = styled.select.attrs<{
    title?: string;
    "aria-label"?: string;
}>(props => ({
    title: props.title ?? "Select",
    "aria-label": props["aria-label"] ?? props.title ?? "Select",
}))`
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;
    background-color: white;
    height: 31px;
    min-width: 150px;
`;

const TableContainer = styled.div`
    margin-top: 12px;
    margin-bottom: 16px;
    overflow-x: auto;
    overflow-y: visible;
    border: 1px solid rgb(235, 235, 235);
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
`;

const TableHeader = styled.th`
    background-color: #f5f5f5;
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    color: #92a5ba;
    border-bottom: 2px solid #e0e0e0;
`;

const TableRow = styled.tr`
    &:hover {
        background-color: #fafafa;
    }
`;

const TableCell = styled.td`
    padding: 8px 12px;
    border-bottom: 1px solid #e0e0e0;
    color: var(--slate);
`;

const ActionMenuContainer = styled.div`
    text-align: center;
    width: 25px;

    & i {
        display: unset;
    }
`;

const ActionMenu = styled.div`
    ${(props: any) => ({
        ...props.style,
    })};
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    padding: 5px;
    box-shadow: 0 4px 6px 0 rgba(119, 135, 154, 0.3);
    border: solid 1px var(--fog);
    background-color: var(--cloud);
    z-index: 9999;
    min-width: 150px;
`;

const ActionButton = styled.div.attrs(() => ({
    className: "pl-1 pr-1",
}))`
    height: 24px;
    border-radius: 2px;
    border: solid 1px var(--blue);
    background-color: var(--white);
    font-family: Helvetica;
    font-size: 13px;
    font-weight: bold;
    text-align: center;
    color: var(--blue);
    padding-top: 2px;
    margin-bottom: 3px;
    cursor: pointer;
    white-space: nowrap;
`;

const NoOptionsMessage = styled.div`
    padding: 8px 12px;
    font-family: Helvetica;
    font-size: 13px;
    color: var(--slate);
    text-align: center;
    font-style: italic;
`;

const SectionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
`;

const ConsentSectionHeader = styled(SectionHeader)`
    flex-wrap: wrap;
    row-gap: 8px;
`;

const ConsentButtonGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
    flex: 1 1 auto;
    min-width: 0;
`;

/** Auto width so long country names (e.g. Dominican Republic) do not overlap adjacent buttons. */
const ConsentActionButton = styled(PrimaryButtonStyled)`
    width: auto;
    min-width: min-content;
    padding: 0 12px;
    flex-shrink: 0;
`;

const ConsentCancelButton = styled(OutlinedButtonStyled)`
    width: auto;
    min-width: min-content;
    padding: 0 12px;
    flex-shrink: 0;
    white-space: nowrap;
`;

const InfoButton = styled.button`
    background: none;
    border: none;
    color: var(--blue);
    cursor: pointer;
    font-size: 18px;
    font-weight: bold;
    padding: 4px 8px;
    transition: all 0.2s;

    &:hover {
        color: var(--dark-blue);
    }
`;

const InfoPopupContainer = styled.div`
    position: relative;
`;

const InfoModalContent = styled.div`
    position: fixed;
    margin-top: 8px;
    background: white;
    border-radius: 8px;
    padding: 20px;
    width: 400px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10000;
    border: 1px solid #e0e0e0;
    max-height: 500px;
    overflow-y: auto;
`;

const InfoModalHeader = styled.div`
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: bold;
    color: var(--slate);
`;

const InfoModalBody = styled.div`
    color: var(--slate);
    font-size: 14px;
`;

const InfoRow = styled.div`
    display: grid;
    grid-template-columns: 150px 1fr;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
        border-bottom: none;
    }
`;

const InfoLabel = styled.div`
    font-weight: 600;
    color: #92a5ba;
`;

const InfoValue = styled.div`
    color: var(--slate);
`;

const InfoValueColored = styled.div<{ isGreen?: boolean }>`
    color: ${props => (props.isGreen ? "#28a745" : "#dc3545")};
    font-weight: 600;
`;

type HoverIconProps = {
    disabled?: boolean;
};

const HoverIcon = styled(Icon)<HoverIconProps>`
    &&& {
        width: 25px;
        height: 16px;
        color: ${props => (!props.disabled ? "var(--blue)" : "var(--fog)")};
        &:hover {
            cursor: ${props => (!props.disabled ? "pointer" : "default")};
        }
    }
`;

const ActionButtonStyled = styled.button`
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    border: 1px solid var(--blue);
    background-color: white;
    color: var(--blue);
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;

    &:hover {
        background-color: var(--blue);
        color: white;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        border-color: var(--fog);
        color: var(--fog);

        &:hover {
            background-color: white;
            color: var(--fog);
        }
    }
`;

const StatusBadge = styled.span<{ isSigned: boolean }>`
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    background-color: ${props => (props.isSigned ? "#d4edda" : "#fff3cd")};
    color: ${props => (props.isSigned ? "#155724" : "#856404")};
`;

export type ManualFixContentProps = {
    personnelId: number;
    personnel: any;
    isStandalone?: boolean;
    showActionButtons?: boolean;
    onActionStateChange?: (state: ManualFixActionState) => void;
    onClose?: () => void;
};

export type ManualFixActionState = {
    consentFormAction: string;
    statusChangeAction: string;
    newStatus: string;
    selectedResendConsentFormId: string;
    selectedCancelConsentFormId: string;
};

type BgcMenuProps = {
    bgc: BackgroundCheckStatus;
    personnelId: number;
    onActionComplete?: () => void;
};

type QuestionnaireHistory = {
    personnelId: number;
    questionnaires: QuestionnaireItem[];
    totalCount: number;
    activeQuestionnaire: QuestionnaireItem | null;
};

type QuestionnaireItem = {
    createdDate: string;
    completedDate: string | null;
    completed: boolean;
    countryLiveIn: string;
    stateLiveIn: string;
    cityLiveIn: string | null;
    countryWorkIn: string;
    stateWorkIn: string;
    cityWorkIn: string | null;
    jurisdictions: string[];
    language: string;
    over18: string;
    exists: boolean;
    active: string;
    questionnaireId: number;
};

type ConsentFormStatus = {
    id: number;
    jurisdiction: string;
    formCategory: string;
    isSigned: boolean;
    completedDate?: string | null;
    sentDate?: string | null;
    adobeEnvelopeId?: string | null;
    signedFileName?: string | null;
    unsignedFileName?: string | null;
    downloadUrl?: string | null;
    active: string;
    statusText?: string;
};

type ConsentFormsStatusResponse = {
    personnelId: number;
    backgroundCheckConsentForms: ConsentFormStatus[];
    drugTestConsentForms: ConsentFormStatus[];
};

type QuestionnaireInfoPopupProps = {
    questionnaire: QuestionnaireItem;
};

const QuestionnaireInfoPopup: React.FC<QuestionnaireInfoPopupProps> = ({ questionnaire }) => {
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    const buttonRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + 8,
                left: rect.right - 400, // Align right edge of popup with button
            });
        }
    }, []);

    return (
        <div ref={buttonRef}>
            <InfoModalContent style={{ top: `${position.top}px`, left: `${position.left}px` }}>
                <InfoModalHeader>Questionnaire Details</InfoModalHeader>
                <InfoModalBody>
                    <InfoRow>
                        <InfoLabel>Country Live In:</InfoLabel>
                        <InfoValue>
                            {questionnaire?.countryLiveIn
                                ? FormUtil.formatJurisdiction(questionnaire.countryLiveIn)
                                : "N/A"}
                        </InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>State Live In:</InfoLabel>
                        <InfoValue>{questionnaire?.stateLiveIn || "N/A"}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>Country Work In:</InfoLabel>
                        <InfoValue>
                            {questionnaire?.countryWorkIn
                                ? FormUtil.formatJurisdiction(questionnaire.countryWorkIn)
                                : "N/A"}
                        </InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>State Work In:</InfoLabel>
                        <InfoValue>{questionnaire?.stateWorkIn || "N/A"}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>Language:</InfoLabel>
                        <InfoValue>
                            {questionnaire?.language
                                ? questionnaire.language.charAt(0).toUpperCase() +
                                  questionnaire.language.slice(1)
                                : "N/A"}
                        </InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>Over 18 Years Old:</InfoLabel>
                        <InfoValueColored isGreen={questionnaire?.over18 === "yes"}>
                            {questionnaire?.over18 === "yes" ? "Yes" : "No"}
                        </InfoValueColored>
                    </InfoRow>
                </InfoModalBody>
            </InfoModalContent>
        </div>
    );
};

const BgcActionMenu: React.FC<BgcMenuProps> = ({ bgc, personnelId, onActionComplete }) => {
    const [show, setShow] = useState(false);
    const target = useRef(null);
    const showAlert = useAlert();

    // Check if status and result are both N/A or empty
    const hasNoData =
        (!bgc.status || bgc.status === "N/A") && (!bgc.result || bgc.result === "N/A");

    const handleResendBgc = async () => {
        try {
            await axios.post(
                `${MANUAL_FIX_API_URL}/background-check/resend/${personnelId}?jurisdiction=${encodeURIComponent(
                    bgc.jurisdiction,
                )}`,
            );
            showAlert("Background check resent successfully", "success");
            setShow(false);
            if (onActionComplete) {
                onActionComplete();
            }
        } catch (error) {
            console.error("Error resending background check:", error);
            showAlert("Error resending background check", "error");
        }
    };

    const handleCancelBgc = async () => {
        try {
            await axios.post(
                `${MANUAL_FIX_API_URL}/background-check/cancel/${personnelId}?jurisdiction=${encodeURIComponent(
                    bgc.jurisdiction,
                )}`,
            );
            showAlert("Background check cancelled successfully", "success");
            setShow(false);
            if (onActionComplete) {
                onActionComplete();
            }
        } catch (error) {
            console.error("Error cancelling background check:", error);
            showAlert("Error cancelling background check", "error");
        }
    };

    const handleCompleteBgc = async () => {
        try {
            await axios.post(
                `${MANUAL_FIX_API_URL}/background-check/complete/${personnelId}?jurisdiction=${encodeURIComponent(
                    bgc.jurisdiction,
                )}`,
            );
            showAlert("Background check completed successfully", "success");
            setShow(false);
            if (onActionComplete) {
                onActionComplete();
            }
        } catch (error) {
            console.error("Error completing background check:", error);
            showAlert("Error completing background check", "error");
        }
    };

    return (
        <ActionMenuContainer>
            <div ref={target} onClick={() => setShow(!show)}>
                <HoverIcon iconName={show ? "fa-times-circle" : "fa-ellipsis-v"} />
            </div>

            {show && (
                <Overlay
                    target={target.current}
                    show={show}
                    placement="bottom"
                    onHide={() => setShow(false)}
                    rootClose={true}
                    rootCloseEvent="click"
                    flip={true}
                    container={document.body}
                >
                    {props => (
                        <ActionMenu {...props}>
                            {hasNoData ? (
                                <NoOptionsMessage>No options available</NoOptionsMessage>
                            ) : (
                                <>
                                    <ActionButton onClick={handleResendBgc}>
                                        Resend BGC
                                    </ActionButton>
                                    <ActionButton onClick={handleCompleteBgc}>
                                        Complete BGC
                                    </ActionButton>
                                    <ActionButton onClick={handleCancelBgc}>
                                        Cancel BGC
                                    </ActionButton>
                                </>
                            )}
                        </ActionMenu>
                    )}
                </Overlay>
            )}
        </ActionMenuContainer>
    );
};

type ConsentFormMenuProps = {
    form: ConsentFormStatus;
    personnelId: number;
    onActionComplete?: () => void;
};

const ConsentFormActionMenu: React.FC<ConsentFormMenuProps> = ({
    form,
    personnelId,
    onActionComplete,
}) => {
    const [show, setShow] = useState(false);
    const target = useRef(null);
    const showAlert = useAlert();

    const handleResendConsentForm = async () => {
        try {
            await axios.post(
                `${MANUAL_FIX_API_URL}/consent-form/resend/${personnelId}?consentFormId=${form.id}`,
            );
            showAlert("Consent form resent successfully", "success");
            setShow(false);
            if (onActionComplete) {
                onActionComplete();
            }
        } catch (error) {
            console.error("Error resending consent form:", error);
            const errorMessage =
                error?.response?.data?.message || "Error resending consent form. Please try again.";
            showAlert(errorMessage, "danger");
        }
    };

    const handleCancelConsentForm = async () => {
        if (!form.id) {
            showAlert("Cannot cancel consent form: missing form ID", "danger");
            setShow(false);
            return;
        }
        try {
            await axios.post(
                `${MANUAL_FIX_API_URL}/consent-form/cancel/${personnelId}?consentFormId=${form.id}`,
            );
            showAlert("Consent form cancelled successfully", "success");
            setShow(false);
            if (onActionComplete) {
                onActionComplete();
            }
        } catch (error) {
            console.error("Error cancelling consent form:", error);
            const errorMessage =
                error?.response?.data?.message ||
                "Error cancelling consent form. Please try again.";
            showAlert(errorMessage, "danger");
        }
    };

    return (
        <ActionMenuContainer>
            <div ref={target} onClick={() => setShow(!show)}>
                <HoverIcon iconName={show ? "fa-times-circle" : "fa-ellipsis-v"} />
            </div>

            {show && (
                <Overlay
                    target={target.current}
                    show={show}
                    placement="bottom"
                    onHide={() => setShow(false)}
                    rootClose={true}
                    rootCloseEvent="click"
                    flip={true}
                    container={document.body}
                >
                    {props => (
                        <ActionMenu {...props}>
                            <ActionButton onClick={handleResendConsentForm}>
                                Resend Consent Form
                            </ActionButton>
                            <ActionButton onClick={handleCancelConsentForm}>
                                Cancel Consent Form
                            </ActionButton>
                        </ActionMenu>
                    )}
                </Overlay>
            )}
        </ActionMenuContainer>
    );
};

export const ManualFixContent: React.FC<ManualFixContentProps> = ({
    personnelId,
    personnel,
    isStandalone = true,
    showActionButtons = true,
    onActionStateChange,
    onClose,
}) => {
    const showAlert = useAlert();
    const { dispatch } = useGlobalModal();
    const [sosReason, setSosReason] = useState<string>("");
    const [personnelData, setPersonnelData] = useState<any>(null);
    const [consentForms, setConsentForms] = useState<any[]>([]);
    const [consentFormsStatus, setConsentFormsStatus] = useState<ConsentFormStatus[]>([]);
    const [statusOptions, setStatusOptions] = useState<Lookup[]>([]);
    const [
        backgroundCheckStatusList,
        setBackgroundCheckStatusList,
    ] = useState<BackgroundCheckStatusList | null>(null);
    const [questionnaireHistory, setQuestionnaireHistory] = useState<QuestionnaireHistory | null>(
        null,
    );
    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<QuestionnaireItem | null>(
        null,
    );

    // Local state for actions
    const [consentFormAction, setConsentFormAction] = useState<string>("");
    const [statusChangeAction, setStatusChangeAction] = useState<string>("");
    const [newStatus, setNewStatus] = useState<string>("");
    const [selectedResendConsentFormId, setSelectedResendConsentFormId] = useState<string>("");
    const [selectedCancelConsentFormId, setSelectedCancelConsentFormId] = useState<string>("");

    // Notify parent of action state changes
    useEffect(() => {
        if (onActionStateChange) {
            onActionStateChange({
                consentFormAction,
                statusChangeAction,
                newStatus,
                selectedResendConsentFormId,
                selectedCancelConsentFormId,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        consentFormAction,
        statusChangeAction,
        newStatus,
        selectedResendConsentFormId,
        selectedCancelConsentFormId,
    ]);

    useEffect(() => {
        // Check if personnel data has roster.statuses with SOS status
        if (personnel?.roster?.statuses && Array.isArray(personnel.roster.statuses)) {
            const sosStatus = personnel.roster.statuses.find(
                (status: any) => status.status?.status === "SOS" && status.active === "ACTIVE",
            );

            if (sosStatus && sosStatus.comment) {
                setSosReason(sosStatus.comment);
            } else {
                setSosReason("Moved to SOS");
            }
        } else if (personnel?.roster?.id) {
            // Fallback: Get activity feed to find SOS reason
            getPersonnelActivityFeed(personnel.roster.id)
                .then(response => {
                    if (response && Array.isArray(response)) {
                        // Find the most recent SOS entry
                        const sosEntry = response.find(
                            (item: any) =>
                                item.status === "SOS" || item.description?.includes("SOS"),
                        );
                        if (sosEntry) {
                            setSosReason(sosEntry.description || "Moved to SOS");
                        }
                    }
                })
                .catch(() => {
                    setSosReason("Moved to SOS");
                });
        }

        // Get full personnel data if not provided or if roster.statuses is missing
        if (personnelId && (!personnel?.email || !personnel?.roster?.statuses)) {
            getPersonnelById(personnelId.toString())
                .then(response => {
                    setPersonnelData(response);
                    // Also check for SOS reason in the fetched data
                    if (response?.roster?.statuses && Array.isArray(response.roster.statuses)) {
                        const sosStatus = response.roster.statuses.find(
                            (status: any) =>
                                status.status?.status === "SOS" && status.active === "ACTIVE",
                        );

                        if (sosStatus && sosStatus.comment) {
                            setSosReason(sosStatus.comment);
                        }
                    }
                })
                .catch(() => {
                    // Use provided personnel data as fallback
                    setPersonnelData(personnel);
                });
        } else {
            setPersonnelData(personnel);
        }
    }, [personnelId, personnel]);

    // Fetch consent forms when modal opens
    useEffect(() => {
        if (personnelId) {
            getActiveConsentForms(personnelId)
                .then(response => {
                    setConsentForms(response || []);
                })
                .catch(error => {
                    console.error("Error fetching consent forms:", error);
                    setConsentForms([]);
                });
        }
    }, [personnelId]);

    // Fetch consent forms status
    useEffect(() => {
        if (personnelId) {
            axios
                .get<ConsentFormsStatusResponse>(
                    `${MANUAL_FIX_API_URL}/consent-forms/history/${personnelId}`,
                )
                .then(response => {
                    console.log("Consent Forms Response:", response.data);
                    if (response.data) {
                        // Combine both background check and drug test consent forms
                        const allForms = [
                            ...(response.data.backgroundCheckConsentForms || []),
                            ...(response.data.drugTestConsentForms || []),
                        ];
                        console.log("Setting consent forms status:", allForms);
                        setConsentFormsStatus(allForms);
                    } else {
                        console.log("No consent forms in response");
                        setConsentFormsStatus([]);
                    }
                })
                .catch(error => {
                    console.error("Error fetching consent forms status:", error);
                    setConsentFormsStatus([]);
                });
        }
    }, [personnelId]);

    useEffect(() => {
        getLkupByName("moveto")
            .then((response: any) => {
                if (response && Array.isArray(response)) {
                    const mapped: Lookup[] = response.map((item: any, index: number) => {
                        const value: string =
                            item.value ?? item.status ?? item.label ?? String(item);
                        const id: string | number = item.id ?? index;
                        const activeFlag: boolean =
                            item.activeFlag !== undefined ? item.activeFlag : true;
                        return createLookup(id, value, activeFlag);
                    });
                    // Ensure labels are preserved
                    const withLabels = mapped.map((lk, i) => ({
                        ...lk,
                        label:
                            (response[i] && (response[i].label ?? response[i].status)) || lk.label,
                    }));
                    setStatusOptions(withLabels);
                }
            })
            .catch(error => {
                console.error("Error fetching status options:", error);
                showAlert(`Error retrieving status options.`, `danger`);
            });
    }, []);

    // Fetch background check statuses
    useEffect(() => {
        if (personnelId) {
            axios
                .get<BackgroundCheckStatusList>(
                    `${MANUAL_FIX_API_URL}/background-check/status/${personnelId}/all`,
                )
                .then(response => {
                    if (response.data) {
                        setBackgroundCheckStatusList(response.data);
                    } else {
                        setBackgroundCheckStatusList(null);
                    }
                })
                .catch(error => {
                    console.error("Error fetching background check statuses:", error);
                    setBackgroundCheckStatusList(null);
                });
        }
    }, [personnelId]);

    // Fetch questionnaire history
    useEffect(() => {
        if (personnelId) {
            axios
                .get<QuestionnaireHistory>(
                    `${MANUAL_FIX_API_URL}/questionnaire/history/${personnelId}`,
                )
                .then(response => {
                    if (response.data) {
                        setQuestionnaireHistory(response.data);
                    } else {
                        setQuestionnaireHistory(null);
                    }
                })
                .catch(error => {
                    console.error("Error fetching questionnaire history:", error);
                    setQuestionnaireHistory(null);
                });
        }
    }, [personnelId]);

    const handleResendQuestionnaire = async () => {
        try {
            await resendQuestionnaire(personnelId);
            showAlert("Questionnaire resent successfully", "success");
            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error("Error resending questionnaire:", error);
            showAlert("Error resending questionnaire, Please contact BIGS Systems", "danger");
        }
    };

    const activeCompletedQuestionnaireJurisdictions = React.useMemo(() => {
        const active = questionnaireHistory?.activeQuestionnaire;
        if (!active || active.active !== "ACTIVE" || !active.completed) {
            return [];
        }
        return active.jurisdictions ?? [];
    }, [questionnaireHistory]);

    const handleSendNewConsentForms = async (jurisdiction?: string) => {
        try {
            const url =
                jurisdiction != null && jurisdiction !== ""
                    ? `${MANUAL_FIX_API_URL}/consent-form/new/${personnelId}?jurisdiction=${encodeURIComponent(
                          jurisdiction,
                      )}`
                    : `${MANUAL_FIX_API_URL}/consent-form/new/${personnelId}`;
            await axios.post(url);
            const successMessage =
                jurisdiction != null && jurisdiction !== ""
                    ? `New consent form sent for ${FormUtil.formatJurisdiction(jurisdiction)}`
                    : "New consent forms sent successfully";
            showAlert(successMessage, "success");
            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error("Error sending new consent form(s):", error);
            const errorMessage = error?.response?.data?.message || "";
            const displayMessage = errorMessage
                ? `Error sending new consent form(s). ${errorMessage}`
                : "Error sending new consent form(s). Please try again.";
            showAlert(displayMessage, "danger");
        }
    };

    const handleCancelPendingConsentForms = () => {
        const cancelCallback = () => {
            cancelConsentForms(personnelId)
                .then(() => {
                    showAlert("Consent forms canceled successfully", "success");
                    if (onClose) {
                        onClose();
                    }
                })
                .catch(() => {
                    showAlert(
                        "Error canceling consent forms, Please contact BIGS Systems",
                        "danger",
                    );
                });
        };

        dispatch({
            type: "openAreYouSureModal",
            yesCallback: cancelCallback,
            content: `Are you sure you want to cancel all pending consent forms for ${personnelData?.firstName} ${personnelData?.lastName}? This action cannot be undone.`,
        });
    };

    return (
        <ContentContainer isStandalone={isStandalone}>
            {isStandalone && (
                <PersonnelInfoSection>
                    <PersonnelNameStyle>
                        {personnelData?.lastName}, {personnelData?.firstName} (
                        {personnelData?.email})
                    </PersonnelNameStyle>
                    {sosReason && <SosReasonStyle>{sosReason}</SosReasonStyle>}
                </PersonnelInfoSection>
            )}

            <CategorySection>
                <SectionHeader>
                    <CategoryTitle>Questionnaire Info</CategoryTitle>
                    {showActionButtons && (
                        <PrimaryButtonStyled
                            onClick={handleResendQuestionnaire}
                            height={26}
                            width={160}
                        >
                            Resend Questionnaire
                        </PrimaryButtonStyled>
                    )}
                </SectionHeader>
                <TableContainer>
                    <StyledTable>
                        <thead>
                            <tr>
                                <TableHeader>Created Date</TableHeader>
                                <TableHeader>Completed Date</TableHeader>
                                <TableHeader>Active</TableHeader>
                                <TableHeader>Info</TableHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {questionnaireHistory &&
                            questionnaireHistory.questionnaires &&
                            questionnaireHistory.questionnaires.length > 0 ? (
                                questionnaireHistory.questionnaires.map(q => {
                                    const createdDate = DateUtil.formatDate(
                                        q.createdDate,
                                        "MM/DD/YYYY h:mm A",
                                    );
                                    const completedDate = q.completed
                                        ? DateUtil.formatDate(q.completedDate, "MM/DD/YYYY h:mm A")
                                        : "N/A";

                                    const isOpen =
                                        selectedQuestionnaire?.questionnaireId ===
                                        q.questionnaireId;

                                    // Check if questionnaire has meaningful data to display
                                    const hasData =
                                        q &&
                                        (q.countryLiveIn ||
                                            q.stateLiveIn ||
                                            q.countryWorkIn ||
                                            q.stateWorkIn ||
                                            q.language ||
                                            q.over18);

                                    return (
                                        <TableRow key={q.questionnaireId}>
                                            <TableCell>{createdDate}</TableCell>
                                            <TableCell>{completedDate}</TableCell>
                                            <TableCell>{q.active}</TableCell>
                                            <TableCell>
                                                <InfoPopupContainer>
                                                    {hasData && (
                                                        <InfoButton
                                                            onClick={() =>
                                                                setSelectedQuestionnaire(
                                                                    isOpen ? null : q,
                                                                )
                                                            }
                                                            style={{
                                                                transform: isOpen
                                                                    ? "rotate(90deg)"
                                                                    : "rotate(0deg)",
                                                                transition: "transform 0.2s ease",
                                                            }}
                                                        >
                                                            {">"}
                                                        </InfoButton>
                                                    )}
                                                    {isOpen && hasData && (
                                                        <QuestionnaireInfoPopup questionnaire={q} />
                                                    )}
                                                </InfoPopupContainer>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={4}
                                        style={{ textAlign: "center", fontStyle: "italic" }}
                                    >
                                        No questionnaires found
                                    </TableCell>
                                </TableRow>
                            )}
                        </tbody>
                    </StyledTable>
                </TableContainer>
            </CategorySection>

            <CategorySection>
                <ConsentSectionHeader>
                    <CategoryTitle>Consent Form</CategoryTitle>
                    {showActionButtons && (
                        <ConsentButtonGroup>
                            {activeCompletedQuestionnaireJurisdictions.length === 1 && (
                                <ConsentActionButton
                                    onClick={() => handleSendNewConsentForms()}
                                    height={26}
                                    width={0}
                                >
                                    Send New Consent Form
                                </ConsentActionButton>
                            )}
                            {activeCompletedQuestionnaireJurisdictions.length >= 2 && (
                                <>
                                    <ConsentActionButton
                                        onClick={() => handleSendNewConsentForms()}
                                        height={26}
                                        width={0}
                                    >
                                        Send New Consent Forms
                                    </ConsentActionButton>
                                    {activeCompletedQuestionnaireJurisdictions.map(jurisdiction => (
                                        <ConsentActionButton
                                            key={jurisdiction}
                                            onClick={() => handleSendNewConsentForms(jurisdiction)}
                                            height={26}
                                            width={0}
                                        >
                                            {`Send - ${FormUtil.formatJurisdiction(jurisdiction)}`}
                                        </ConsentActionButton>
                                    ))}
                                </>
                            )}
                            <ConsentCancelButton
                                onClick={handleCancelPendingConsentForms}
                                height={26}
                                width={0}
                            >
                                Cancel Pending Consent Forms
                            </ConsentCancelButton>
                        </ConsentButtonGroup>
                    )}
                </ConsentSectionHeader>

                {/* Consent Forms Status Table */}
                <TableContainer>
                    <StyledTable>
                        <thead>
                            <tr>
                                <TableHeader>Location</TableHeader>
                                <TableHeader>Status</TableHeader>
                                <TableHeader>Complete Date</TableHeader>
                                <TableHeader>Form</TableHeader>
                                <TableHeader />
                            </tr>
                        </thead>
                        <tbody>
                            {consentFormsStatus && consentFormsStatus.length > 0 ? (
                                consentFormsStatus.map(form => {
                                    const completeDate = form.completedDate
                                        ? DateUtil.formatDate(
                                              form.completedDate,
                                              "MM/DD/YYYY h:mm A",
                                          )
                                        : "N/A";

                                    return (
                                        <TableRow key={form.id}>
                                            <TableCell>
                                                {FormUtil.formatJurisdiction(form.jurisdiction)}
                                            </TableCell>
                                            <TableCell>
                                                <div>{form.statusText || "N/A"}</div>
                                            </TableCell>
                                            <TableCell>{completeDate}</TableCell>
                                            <TableCell>
                                                {form.isSigned && form.signedFileName ? (
                                                    <div>
                                                        <HoverIcon
                                                            color="var(--blue)"
                                                            iconName="fa-download"
                                                            onClick={() => {
                                                                if (form.signedFileName) {
                                                                    downloadFile(
                                                                        form.signedFileName,
                                                                    ).then(data => {
                                                                        FileUtil.openFileInNewTab(
                                                                            data,
                                                                        );
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>
                                                {showActionButtons && (
                                                    <ConsentFormActionMenu
                                                        form={form}
                                                        personnelId={personnelId}
                                                        onActionComplete={onClose}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={5}
                                        style={{ textAlign: "center", fontStyle: "italic" }}
                                    >
                                        No consent forms found
                                    </TableCell>
                                </TableRow>
                            )}
                        </tbody>
                    </StyledTable>
                </TableContainer>
            </CategorySection>

            <CategorySection>
                <CategoryTitle>Background Checks</CategoryTitle>
                {backgroundCheckStatusList?.backgroundChecks &&
                    backgroundCheckStatusList.backgroundChecks.length > 0 && (
                        <TableContainer>
                            <StyledTable>
                                <thead>
                                    <tr>
                                        <TableHeader>Jurisdiction</TableHeader>
                                        <TableHeader>Status</TableHeader>
                                        <TableHeader>Result</TableHeader>
                                        <TableHeader>Date/Time</TableHeader>
                                        <TableHeader />
                                    </tr>
                                </thead>
                                <tbody>
                                    {backgroundCheckStatusList.backgroundChecks.map((bgc, idx) => {
                                        const displayDate =
                                            bgc.sterlingUpdatedTs || bgc.sterlingCreatedTs;
                                        const formattedDate = displayDate
                                            ? DateUtil.formatDate(displayDate, "MM/DD/YYYY h:mm A")
                                            : "N/A";

                                        return (
                                            <TableRow
                                                key={
                                                    bgc.rosterStatusId != null
                                                        ? `bgc-rs-${bgc.rosterStatusId}`
                                                        : `bgc-${idx}-${bgc.jurisdiction}`
                                                }
                                            >
                                                <TableCell>
                                                    {FormUtil.formatJurisdiction(bgc.jurisdiction)}
                                                </TableCell>
                                                <TableCell>{bgc.status || "N/A"}</TableCell>
                                                <TableCell>{bgc.result || "N/A"}</TableCell>
                                                <TableCell>{formattedDate}</TableCell>
                                                <TableCell>
                                                    {showActionButtons && (
                                                        <BgcActionMenu
                                                            bgc={bgc}
                                                            personnelId={personnelId}
                                                            onActionComplete={onClose}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </tbody>
                            </StyledTable>
                        </TableContainer>
                    )}
            </CategorySection>

            {showActionButtons && (
                <CategorySection>
                    <CategoryTitle>Status Change</CategoryTitle>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <StyledRadioButtonText>Move personnel to new status:</StyledRadioButtonText>
                        <DropdownSelect
                            value={newStatus}
                            onChange={e => setNewStatus(e.target.value)}
                            aria-label="New status"
                            title="New status"
                            style={{ minWidth: "200px" }}
                        >
                            <option value="">Select Status</option>
                            {statusOptions.map(option => (
                                <option key={option.id} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </DropdownSelect>
                        <PrimaryButtonStyled
                            onClick={async () => {
                                if (!newStatus) {
                                    showAlert("Please select a status", "warning");
                                    return;
                                }
                                try {
                                    await axios.post(
                                        `${MANUAL_FIX_API_URL}/status-change/${personnelId}?newStatus=${encodeURIComponent(
                                            newStatus,
                                        )}`,
                                    );
                                    showAlert("Status changed successfully", "success");
                                    setNewStatus("");
                                    if (onClose) {
                                        onClose();
                                    }
                                } catch (error) {
                                    console.error("Error changing status:", error);
                                    showAlert("Error changing status. Please try again.", "error");
                                }
                            }}
                            height={31}
                            width={80}
                            disabled={!newStatus}
                        >
                            Submit
                        </PrimaryButtonStyled>
                    </div>
                </CategorySection>
            )}
        </ContentContainer>
    );
};
